import type { FirstClassDocument, Highlight } from '../../types';
import {
  PromptModel,
  PromptResponseAction,
  PromptReturnType,
  PromptScopeType,
  PromptsVersion,
  PromptType,
} from './constants';

export type AiDisallowedReason =
  | 'book-feature-disabled'
  | 'ai-disabled';

export type PromptScope = {
  description?: string;
  title: string;
  type: PromptScopeType;
  canRender: boolean;
  isCustomizable?: boolean;
  prompts: Prompt[];
};

export type Prompt = {
  model: PromptModel;
  system: string;
  template: string;
  title: string;
  type: PromptType;
  description?: string;
  position: number;
  returnType: PromptReturnType;

  /*
    This exists for two reasons:
    1. Different places can trigger Ghostreader prompts. When a prompt is being executed, the details get
      stored in Zustand. When read from Zustand, we know what we should do with the result/response.
    2. Initially we weren't sure if this could become configurable later. This never happened and is no
      longer applicable.

    The backend never uses this (other than storing & serving the prompts of course).
  */
  responseAction: PromptResponseAction;
  isDefaultPrompt?: boolean;
  isCustomizable?: boolean;
  isEnabled?: boolean;
};

export type Prompts = {
  version: PromptsVersion;
  data: PromptScope[];
};

export type OverriddenPrompts = {
  [promptScopeType: string]: { [promptType: string]: Prompt; };
};

export type CreatedPrompts = {
  [promptScopeType: string]: Prompt[];
};

export enum ChatSkillScope {
  Global = 'GLOBAL',
  Document = 'DOCUMENT',
  Passage = 'PASSAGE',
  Word = 'WORD',
}

export enum ChatSkillStatus {
  Active = 'active',
  Staff = 'staff',
  Draft = 'draft',
  Inactive = 'inactive',
}

export type DefaultChatSkill = {
  slug: string;
  name: string;
  description: string;
  keywords: string[];
  instructions: string;
  scopes: ChatSkillScope[];
  status: ChatSkillStatus;
};

export type ChatSkill = {
  slug: string;
  name: string;
  description: string;
  instructions: string;
  scopes: ChatSkillScope[];
  keywords?: string[];
  model?: PromptModel;
  isEnabled?: boolean;
  isDefault?: boolean;
};

export type ChatSkillDeletionMarker = { deleted: true; };

export type OverriddenChatSkill = ChatSkill | ChatSkillDeletionMarker;

export type OverriddenChatSkills = {
  [slug: string]: OverriddenChatSkill;
};

export type CreatedChatSkills = ChatSkill[];

export function isChatSkillDeletionMarker(value: OverriddenChatSkill | undefined): value is ChatSkillDeletionMarker {
  return value !== undefined && 'deleted' in value && value.deleted === true;
}

export type PromptExecutionPayload = {
  documentId: FirstClassDocument['id'];
  expandedSelection?: string;
  focusIndicator?: string;
  highlightId?: Highlight['id'];
  // model is from existing preset prompts and modelChoice is from the chat
  // keep both for now - backwards compatibility
  model: string;
  modelChoice?: string;
  prompt: string;
  promptTitle?: string;
  responseAction: PromptResponseAction;
  selection?: string;
  serviceTier?: 'priority';
  systemPrompt?: string;
};
